// src/components/booking/sections/HeroSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Star, 
  Users, 
  Calendar, 
  MapPin, 
  TrendingUp,
  Heart,
  Share2,
  Play,
  Pause
} from 'lucide-react';
import { bookingService } from '../../../services/bookingService';

// src/components/booking/sections/BookingHeroSection.jsx - Updated to use featured listings
export const BookingHeroSection = ({ 
  bookData, 
  bookingSettings, 
  customStyling, 
  bookId, 
  onCTAClick 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isHovered, setIsHovered] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });

  // Load featured listings
  useEffect(() => {
    const loadFeaturedListings = async () => {
      if (!bookId) return;
      
      try {
        const listings = await bookingService.getFeaturedListings(bookId, 5);
        setFeaturedListings(listings);
      } catch (error) {
        console.error('Error loading featured listings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedListings();
  }, [bookId]);

  useEffect(() => {
    const checkMobileDevice = () => {
      const isMobile = window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
      setIsMobileDevice(isMobile);
    };
    
    checkMobileDevice();
    window.addEventListener('resize', checkMobileDevice);
    return () => window.removeEventListener('resize', checkMobileDevice);
  }, []);

  // Prepare slideshow images from featured listings
  const slideImages = featuredListings
    .filter(listing => listing.images?.length > 0)
    .map(listing => ({
      url: listing.images[0].url,
      listing: listing
    }))
    .concat(
      // Fallback images if no featured listings
      [bookingSettings?.heroImage, bookData?.coverImageUrl, bookData?.heroImage]
        .filter(Boolean)
        .map(url => ({ url, listing: null }))
    )
    .slice(0, 5);

  // Auto-advance slideshow
  useEffect(() => {
    if (!isPlaying || slideImages.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slideImages.length);
    }, 30000);

    return () => clearInterval(timer);
  }, [isPlaying, slideImages.length]);

  // Get featured list stats
  const getFeaturedStats = () => {
    if (featuredListings.length === 0) {
      return [
        { icon: Users, label: 'Happy Clients', value: '1000+' },
        { icon: Star, label: 'Average Rating', value: '4.8' },
        { icon: Calendar, label: 'Years Experience', value: '5+' }
      ];
    }

    const totalBookings = featuredListings.reduce((sum, listing) => 
      sum + (listing.analytics?.bookings || 0), 0
    );
    
    const avgRating = featuredListings.reduce((sum, listing) => 
      sum + (listing.rating || 4.5), 0
    ) / featuredListings.length;

    return [
      { 
        icon: MapPin, 
        label: 'Featured Places', 
        value: featuredListings.length.toString(),
        subtitle: 'Premium locations'
      },
      { 
        icon: Star, 
        label: 'Average Rating', 
        value: avgRating.toFixed(1),
        subtitle: 'Customer satisfaction'
      },
      { 
        icon: Users, 
        label: 'Total Bookings', 
        value: totalBookings > 0 ? totalBookings.toString() : '500+',
        subtitle: 'Happy customers'
      }
    ];
  };

  const handleTouchStart = (e) => {
    if (!isMobileDevice) return;
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    if (!isMobileDevice) return;
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = () => {
    if (!isMobileDevice || !touchStart.x || !touchEnd.x) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    const minSwipeDistance = 50;
    
    // Only process horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > minSwipeDistance && deltaY < 100) {
      if (deltaX > 0) {
        // Swipe left - next slide
        setCurrentSlide(prev => (prev + 1) % slideImages.length);
      } else {
        // Swipe right - previous slide
        setCurrentSlide(prev => (prev - 1 + slideImages.length) % slideImages.length);
      }
    }
    
    // Reset touch positions
    setTouchStart({ x: 0, y: 0 });
    setTouchEnd({ x: 0, y: 0 });
  };

  const currentSlideData = slideImages[currentSlide];
  const featuredStats = getFeaturedStats();

  return (
    <section className="relative h-[70vh] md:h-[90vh] max-h-[600px] overflow-hidden">
      {/* Logo - Top Left */}
      <div className="absolute top-6 left-6 z-30">
        {bookData?.logoUrl ? (
          <img
            src={bookData.logoUrl}
            alt={bookData?.botName || 'Logo'}
            className="h-12 w-auto max-w-[200px] object-contain backdrop-blur-sm rounded-full px-4 py-2"
            style={{
              boxShadow: `
                inset 0 1px 3px rgba(0, 0, 0, 0.3),
                0 0 8px rgba(255, 255, 255, 0.3)
              `,
            }}
          />
        ) : (
          <div
            className="px-4 py-2 rounded-lg font-bold text-white bg-white/10 backdrop-blur-sm border border-white/20"
            style={{
              color: customStyling?.primaryColor || '#ffffff',
              boxShadow: `
                inset 0 1px 3px rgba(0, 0, 0, 0.3),
                0 0 8px rgba(255, 255, 255, 0.2)
              `,
            }}
          >
            {bookData?.botName || 'Logo'}
          </div>
        )}
      </div>

      {/* Featured Listing Badge */}
      {currentSlideData?.listing && (
        <div className="absolute top-6 right-6 z-30">
          <div className="bg-white/10 backdrop-blur-sm text-yellow-500/50 px-3 py-2 rounded-full flex items-center gap-2 text-sm font-medium">
            <Star className="h-4 w-4 fill-current" />
          </div>
        </div>
      )}

      {/* Slideshow Container */}
      <div className="relative h-full">
        {slideImages.length > 0 ? (
          slideImages.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.url}
                alt={slide.listing?.title || `Slide ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/60"></div>
            </div>
          ))
        ) : (
          <div 
            className="w-full h-full bg-gradient-to-r from-blue-600 to-emerald-600"
            style={{
              backgroundColor: customStyling?.primaryColor || '#10b981'
            }}
          >
            <div className="absolute inset-0 bg-black/20"></div>
          </div>
        )}

        {/* Slideshow Controls */}
        {slideImages.length > 1 && (
          <>
           {/* Navigation Arrows - Only show on hover for desktop, hidden on mobile */}
            {!isMobileDevice && isHovered && (
              <>
                <button
                  onClick={() => setCurrentSlide(prev => (prev - 1 + slideImages.length) % slideImages.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-200 opacity-0 group-hover:opacity-100"
                  style={{ 
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 300ms ease-in-out' 
                  }}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                <button
                  onClick={() => setCurrentSlide(prev => (prev + 1) % slideImages.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                  style={{ 
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 300ms ease-in-out' 
                  }}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Play/Pause Button */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="absolute bottom-10 right-6 z-20 p-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-200"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>

            {/* Slide Indicators */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {slideImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full border border-white/50 transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-white' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Content Overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="text-center text-white px-4 max-w-6xl mx-auto">
          {/* Main Content */}
          <div className="mb-12">
            {/* Dynamic title based on current slide */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 drop-shadow-lg">
              {currentSlideData?.listing?.title || bookData?.title || bookData?.botName || 'Discover Amazing Places'}
            </h1>
            
            {/* Price display for featured listings */}
            {currentSlideData?.listing && (
              <div className="mb-6">
                <div className="inline-block bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl px-6 py-3">
                  <span className="text-2xl font-bold">
                    R{currentSlideData.listing.price}
                  </span>
                  <span className="text-lg opacity-90"> / night</span>
                </div>
              </div>
            )}

            {/* CTA Button */}
            {onCTAClick && (
              <button
                onClick={onCTAClick}
                className="inline-flex items-center gap-2 px-8 py-2 text-md font-semibold rounded-full transition-all duration-200 hover:scale-105 shadow-xl"
                style={{
                  backgroundColor: customStyling?.primaryColor || '#10b981',
                  color: customStyling?.textColor || '#ffffff',
                  opacity: 10
                }}
              >
                {currentSlideData?.listing ? 'View Details' : 'Explore Now'}
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Featured Stats */}
          <div className="grid grid-cols-3 md:grid-cols-3 gap-3 md:gap-6 max-w-4xl mx-auto">
            {featuredStats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div 
                  key={index}
                  className="group"
                >
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                    <div className="flex items-center justify-center mb-2">
                      <div className="p-3 rounded-full">
                        <IconComponent className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    
                    <div className="text-xl md:text-2xl font-bold mb-2 drop-shadow-md text-center">
                      {stat.value}
                    </div>
                    
                    {/* Hide text labels on mobile, show on desktop */}
                    <div className="hidden md:block">
                      <div className="text-sm font-normal opacity-90 mb-1 text-center">
                        {stat.label}
                      </div>
                      
                      {stat.subtitle && (
                        <div className="text-sm opacity-75 text-center">
                          {stat.subtitle}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile swipe indicator */}
          {isMobileDevice && slideImages.length > 1 && (
            <div className="md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs pointer-events-none">
              Swipe to navigate • {currentSlide + 1} of {slideImages.length}
            </div>
          )}
      </div>
    </section>
  );
};